// SMTP2GO Email Service Integration
// This is a placeholder for the actual SMTP2GO API integration
// In a real implementation, you would use the SMTP2GO API

import logger from '../utils/logger';
const SMTP2GO_API_KEY = process.env.REACT_APP_SMTP2GO_API_KEY || 'your-smtp2go-api-key';
const SMTP2GO_API_URL = 'https://api.smtp2go.com/v3/email/send';

// Helper to map DB ageGroup values to display labels used in emails
const mapAgeGroupForEmail = (ageGroup) => {
  const mapping = { child: 'Ages 3-6', teen: 'Ages 7-12', adult: 'Ages 13+' };
  if (typeof ageGroup === 'string' && (ageGroup.startsWith('Ages') || mapping[ageGroup])) {
    return mapping[ageGroup] || ageGroup;
  }
  return 'Ages 7-12';
};

// Email sending function
export const sendEmail = async (emailData) => {
  try {
    console.log('\u{1F4E7} Sending email via serverless function:', {
      to: emailData.to,
      subject: emailData.subject,
      template: emailData.template
    });

    // Call the appropriate serverless function based on email template
    let endpoint = '';
    let requestBody = {};

    switch (emailData.template) {
      case 'welcome':
        endpoint = '/api/send-welcome-email';
        requestBody = {
          email: emailData.to,
          displayName: emailData.data?.accountName || emailData.data?.name || 'there',
          accountName: emailData.data?.accountName || emailData.data?.name || null,
          profiles: emailData.data?.profiles || null,
          hasCompletedFirstLesson: emailData.data?.hasCompletedFirstLesson || false
        };
        break;
      
      case 'epic3_welcome':
        endpoint = '/api/send-welcome-email';
        requestBody = {
          email: emailData.to,
          displayName: emailData.data?.accountName || emailData.data?.userName || 'there',
          accountName: emailData.data?.accountName || emailData.data?.userName || null,
          profiles: emailData.data?.profiles || null,
          hasCompletedFirstLesson: emailData.data?.hasCompletedFirstLesson || false
        };
        break;
      
      case 'epic3_day4_progress':
        endpoint = '/api/send-day4-progress-email';
        requestBody = {
          email: emailData.to,
          userName: emailData.data?.userName || 'there',
          profileNickname: emailData.data?.profileNickname || 'Learner',
          language: emailData.data?.language || 'Twi',
          ageGroup: mapAgeGroupForEmail(emailData.data?.ageGroup || 'teen'),
          progressData: {
            wordsLearned: emailData.data?.wordsLearned || 0,
            lessonsCompleted: emailData.data?.lessonsCompleted || 0,
            learningStreak: emailData.data?.learningStreak || 0
          },
          testimonial: emailData.data?.testimonial || ''
        };
        break;
      
      case 'epic3_day6_expiry':
        endpoint = '/api/send-day6-expiry-email';
        requestBody = {
          email: emailData.to,
          userName: emailData.data?.userName || 'there',
          language: emailData.data?.language || 'Twi'
        };
        break;
      
      case 'subscription_confirmed':
        endpoint = '/api/send-subscription-confirmed-email';
        requestBody = {
          email: emailData.to,
          parentName: emailData.data?.parentName || 'there',
          planType: emailData.data?.planType || 'monthly',
          amount: emailData.data?.amount || '10.00',
          purchaseDate: emailData.data?.purchaseDate || new Date().toISOString()
        };
        break;
      
      case 'progress_report':
        endpoint = '/api/send-progress-report-email';
        requestBody = {
          email: emailData.to,
          accountName: emailData.data?.accountName || 'there',
          profileNickname: emailData.data?.profileNickname || 'Learner',
          wordsLearned: emailData.data?.wordsLearned || 0,
          lessonsCompleted: emailData.data?.lessonsCompleted || 0,
          xpGained: emailData.data?.xpGained || 0,
          currentStreak: emailData.data?.currentStreak || 0,
          nextModuleId: emailData.data?.nextModuleId,
          language: emailData.data?.language || 'Twi',
          cadence: emailData.data?.cadence || 'weekly'
        };
        break;
      
      case 're_engagement':
        endpoint = '/api/send-reengagement-email';
        requestBody = {
          email: emailData.to,
          accountName: emailData.data?.accountName || 'there',
          language: emailData.data?.language || 'Twi',
          lastIncompleteModuleId: emailData.data?.lastIncompleteModuleId,
          cadence: emailData.data?.cadence || 'weekly'
        };
        break;
      
      case 'family_digest':
        endpoint = '/api/send-family-digest-email';
        requestBody = {
          email: emailData.to,
          accountName: emailData.data?.accountName || 'there',
          profiles: emailData.data?.profiles || [],
          cadence: emailData.data?.cadence || 'weekly'
        };
        break;
      
      default:
        // For unknown templates, simulate
        await new Promise(resolve => setTimeout(resolve, 1000));
        return {
          success: true,
          message_id: `msg_${Date.now()}`,
          message: 'Email sent successfully (simulated)'
        };
    }

    // Make the API call
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    // Get response text first (can only read once)
    const responseText = await response.text();

    if (!response.ok) {
      // Try to get error message from response
      let errorMessage = 'Failed to send email';
      try {
        if (responseText && responseText.trim() !== '') {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorMessage;
        } else {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
      } catch (parseError) {
        // If JSON parse fails, use the text as-is or status
        errorMessage = responseText || `HTTP ${response.status}: ${response.statusText}`;
      }
      logger.error('App', `❌ ❌ Failed to send email: ${errorMessage}`);
      throw new Error(`Serverless function error: ${errorMessage}`);
    }

    // Parse JSON only if response is OK
    let result;
    try {
      if (!responseText || responseText.trim() === '') {
        // Empty response - treat as success
        console.log('\u2705 Email sent successfully via serverless function (empty response)');
        return { success: true };
      }
      result = JSON.parse(responseText);
    } catch (parseError) {
      logger.error('App', 'JSON Parse error:', parseError.message, 'Response text:', responseText.substring(0, 100));
      throw new Error('Invalid response from server');
    }

    console.log('\u2705 Email sent successfully via serverless function:', result);
    return result;

  } catch (error) {
    logger.error('App', '\u274C Failed to send email:', error);
    throw error;
  }
};

// Batch email sending for campaigns
export const sendBatchEmails = async (emailList) => {
  const results = [];
  
  for (const emailData of emailList) {
    try {
      const result = await sendEmail(emailData);
      results.push({ success: true, email: emailData.to, result });
    } catch (error) {
      results.push({ success: false, email: emailData.to, error: error.message });
    }
  }
  
  return results;
};

// Email validation
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Email template data preparation
export const prepareEmailData = (template, userData, additionalData = {}) => {
  return {
    to: userData.email || 'parent@example.com',
    subject: template.subject,
    template: template.template,
    data: {
      ...userData,
      ...additionalData,
      unsubscribeUrl: `${window.location.origin}/unsubscribe?user=${userData.id}`,
      supportUrl: `${window.location.origin}/support`
    }
  };
};

// Email analytics tracking
export const trackEmailEvent = (eventType, emailData, additionalData = {}) => {
  // In a real implementation, this would send analytics data
  console.log('\u{1F4CA} Email Analytics:', {
    event: eventType,
    email: emailData.to,
    template: emailData.template,
    timestamp: new Date().toISOString(),
    ...additionalData
  });
};

// Email delivery status checking (placeholder)
export const checkEmailDeliveryStatus = async (messageId) => {
  // In a real implementation, this would check SMTP2GO delivery status
  console.log('\u{1F4EC} Checking email delivery status for:', messageId);
  
  // Simulate status check
  return {
    message_id: messageId,
    status: 'delivered',
    delivered_at: new Date().toISOString()
  };
};

// Email bounce handling (placeholder)
export const handleEmailBounce = (bounceData) => {
  console.log('\u{1F4E7} Email bounce received:', bounceData);
  
  // In a real implementation, this would:
  // 1. Update user email status
  // 2. Remove from future campaigns
  // 3. Log bounce reason
  // 4. Potentially update user profile
};

// Email unsubscribe handling
export const handleUnsubscribe = async (userId, email) => {
  console.log('\u{1F6AB} User unsubscribed:', { userId, email });
  
  // In a real implementation, this would:
  // 1. Update user preferences
  // 2. Remove from email campaigns
  // 3. Log unsubscribe reason
  // 4. Send confirmation email
  
  return {
    success: true,
    message: 'Successfully unsubscribed from emails'
  };
};

// Legacy service object for backward compatibility
export const smtp2goService = {
  sendWelcomeEmail: async (email, name, accountName = null, profiles = null, hasCompletedFirstLesson = false) => {
    return await sendEmail({
      to: email,
      subject: 'Welcome to SpeakRoots! Your 7-day trial has begun.',
      template: 'welcome',
      data: { 
        name, 
        accountName, 
        profiles,
        hasCompletedFirstLesson
      }
    });
  },
  
  sendSubscriptionConfirmedEmail: async (email, parentName, planType, amount, purchaseDate) => {
    return await sendEmail({
      to: email,
      subject: 'Subscription Confirmed - Welcome to SpeakRoots Premium!',
      template: 'subscription_confirmed',
      data: { 
        parentName,
        planType,
        amount,
        purchaseDate
      }
    });
  },
  
  // Epic 3 email support
  sendEmail: async (emailData) => {
    return await sendEmail(emailData);
  }
};